import { ReactNode, useMemo, useState } from 'react';

export type SimpleFolderRow = {
  id: number;
  parent_id: number | null;
  name: string;
};
export type SimpleItem = {
  id: number;
  folder_id: number | null;
};

type TreeNode<T extends SimpleItem> = {
  id: number;
  name: string;
  children: TreeNode<T>[];
  items: T[];
};

function buildTree<T extends SimpleItem>(
  folders: SimpleFolderRow[],
  items: T[]
): { roots: TreeNode<T>[]; rootItems: T[] } {
  const byId = new Map<number, TreeNode<T>>();
  folders.forEach((f) => byId.set(f.id, { id: f.id, name: f.name, children: [], items: [] }));
  const roots: TreeNode<T>[] = [];
  folders.forEach((f) => {
    const node = byId.get(f.id)!;
    if (f.parent_id === null) roots.push(node);
    else {
      const p = byId.get(f.parent_id);
      if (p) p.children.push(node);
      else roots.push(node);
    }
  });
  const rootItems: T[] = [];
  items.forEach((it) => {
    if (it.folder_id === null) rootItems.push(it);
    else byId.get(it.folder_id)?.items.push(it);
  });
  const sortNode = (n: TreeNode<T>) => {
    n.children.sort((a, b) => a.name.localeCompare(b.name));
    n.children.forEach(sortNode);
  };
  roots.sort((a, b) => a.name.localeCompare(b.name));
  roots.forEach(sortNode);
  return { roots, rootItems };
}

export default function SimpleFolderTree<T extends SimpleItem>({
  folders,
  items,
  renderItem,
  onAddFolder,
  onRenameFolder,
  onDeleteFolder,
  onMoveItem,
  headerLabel = '📚 Classement',
  rootLabel = 'Non classés',
}: {
  folders: SimpleFolderRow[];
  items: T[];
  renderItem: (item: T) => ReactNode;
  onAddFolder: (name: string, parentId: number | null) => void | Promise<void>;
  onRenameFolder: (id: number, name: string) => void | Promise<void>;
  onDeleteFolder: (id: number) => void | Promise<void>;
  onMoveItem: (itemId: number, folderId: number | null) => void | Promise<void>;
  headerLabel?: string;
  rootLabel?: string;
}) {
  const { roots, rootItems } = useMemo(() => buildTree(folders, items), [folders, items]);
  const [expanded, setExpanded] = useState<Set<number>>(
    () => new Set(folders.map((f) => f.id))
  );
  const [addingRoot, setAddingRoot] = useState(false);
  const [rootName, setRootName] = useState('');

  function toggle(id: number) {
    const n = new Set(expanded);
    n.has(id) ? n.delete(id) : n.add(id);
    setExpanded(n);
  }

  function FolderNode({ node, depth }: { node: TreeNode<T>; depth: number }) {
    const [addingChild, setAddingChild] = useState(false);
    const [childName, setChildName] = useState('');
    const [renaming, setRenaming] = useState(false);
    const [newName, setNewName] = useState(node.name);
    const open = expanded.has(node.id);

    return (
      <div
        style={{ paddingLeft: depth === 0 ? 0 : 14 }}
        onDragOver={(e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
        }}
        onDrop={(e) => {
          e.preventDefault();
          const itemId = Number(e.dataTransfer.getData('text/plain'));
          if (itemId) onMoveItem(itemId, node.id);
        }}
      >
        <div className="folder-row">
          <button className="folder-expand" onClick={() => toggle(node.id)} title={open ? 'Replier' : 'Déplier'}>
            {open ? '▾' : '▸'}
          </button>
          <span className="folder-icon">{open ? '📂' : '📁'}</span>
          {renaming ? (
            <input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onBlur={() => {
                if (newName.trim() && newName !== node.name) onRenameFolder(node.id, newName.trim());
                setRenaming(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                if (e.key === 'Escape') {
                  setNewName(node.name);
                  setRenaming(false);
                }
              }}
              style={{ flex: 1, padding: '2px 6px', borderRadius: 4, border: '1px solid var(--c-border)' }}
            />
          ) : (
            <span className="folder-name" onDoubleClick={() => setRenaming(true)} title="Double-clic pour renommer">
              {node.name}
            </span>
          )}
          <span className="folder-count">{node.items.length}</span>
          <div className="folder-actions">
            <button onClick={() => setAddingChild((v) => !v)} title="Nouveau sous-dossier">+</button>
            <button onClick={() => setRenaming(true)} title="Renommer">✎</button>
            <button
              onClick={() => {
                if (confirm(`Supprimer le dossier « ${node.name} » ? Les éléments remontent à la racine.`)) {
                  onDeleteFolder(node.id);
                }
              }}
              title="Supprimer"
            >
              ×
            </button>
          </div>
        </div>

        {addingChild && (
          <div className="folder-add-form" style={{ marginLeft: 30 }}>
            <input
              autoFocus
              placeholder="Nom du sous-dossier"
              value={childName}
              onChange={(e) => setChildName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && childName.trim()) {
                  onAddFolder(childName.trim(), node.id);
                  setChildName('');
                  setAddingChild(false);
                }
                if (e.key === 'Escape') {
                  setAddingChild(false);
                  setChildName('');
                }
              }}
            />
          </div>
        )}

        {open && (
          <div>
            {node.children.map((c) => (
              <FolderNode key={c.id} node={c} depth={depth + 1} />
            ))}
            {node.items.map((it) => (
              <DraggableItem key={it.id} item={it} depth={depth + 1} />
            ))}
          </div>
        )}
      </div>
    );
  }

  function DraggableItem({ item, depth }: { item: T; depth: number }) {
    return (
      <div
        style={{ paddingLeft: depth * 14 + 30 }}
        draggable
        onDragStart={(e) => e.dataTransfer.setData('text/plain', String(item.id))}
      >
        {renderItem(item)}
      </div>
    );
  }

  return (
    <div className="folder-tree">
      <div
        className="folder-tree-header"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const itemId = Number(e.dataTransfer.getData('text/plain'));
          if (itemId) onMoveItem(itemId, null);
        }}
      >
        <strong>{headerLabel}</strong>
        <button
          className="btn btn-ghost"
          onClick={() => setAddingRoot((v) => !v)}
          style={{ padding: '4px 10px', fontSize: 12 }}
        >
          {addingRoot ? 'Annuler' : '+ Dossier racine'}
        </button>
      </div>
      {addingRoot && (
        <div className="folder-add-form">
          <input
            autoFocus
            placeholder="Nom du dossier"
            value={rootName}
            onChange={(e) => setRootName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && rootName.trim()) {
                onAddFolder(rootName.trim(), null);
                setRootName('');
                setAddingRoot(false);
              }
              if (e.key === 'Escape') {
                setAddingRoot(false);
                setRootName('');
              }
            }}
          />
        </div>
      )}
      {roots.length === 0 && rootItems.length === 0 && (
        <div className="card-meta" style={{ padding: 14 }}>
          Aucun élément. Crée un dossier pour organiser.
        </div>
      )}
      {roots.map((r) => (
        <FolderNode key={r.id} node={r} depth={0} />
      ))}
      {rootItems.length > 0 && (
        <div className="folder-root-docs">
          <div className="folder-row" style={{ opacity: 0.6 }}>
            <span className="folder-icon">🗂️</span>
            <span className="folder-name">{rootLabel}</span>
            <span className="folder-count">{rootItems.length}</span>
          </div>
          {rootItems.map((it) => (
            <DraggableItem key={it.id} item={it} depth={0} />
          ))}
        </div>
      )}
    </div>
  );
}
